import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import { toast } from 'react-toastify';
import { getCentreById, getCentreUsers, unassignUserFromCentre } from '@app/services/centre';
import { getRolesByGroupName } from '@app/services/role';
import AddUpdateCentreUserModal from '@app/pages/user/AddUpdateCentreUserModal';
import AssignExistingUserModal from '@app/pages/user/AssignExistingUserModal';
import { confirmAlert } from 'react-confirm-alert';

const ViewCentre = () => {

    let obj: any = {}
    const location = useLocation();
    const id = useDecodedParam('encodedId', '/centers') ?? (location.state as { id?: number })?.id;
    const [isLoading, setLoading] = useState(false);
    const [centresData, setCentresData] = useState(obj);
    const [staffUsers, setStaffUsers] = useState<any[]>([]);
    const [centreRoles, setCentreRoles] = useState<any[]>([]);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showAssignUser, setShowAssignUser] = useState(false);
    const [editUser, setEditUser] = useState<any>(null);
    // Archived centres are read-only: staff/users are listed but cannot be managed
    const isArchived = centresData?.status === 'archived';

    useEffect(() => {
        getCentreDetailsById(id);
        fetchCentreUsers(id);
        fetchCentreRoles();
    },[]);

    const getCentreDetailsById = async (id) => {
        try {
            setLoading(true);
            const resp = await getCentreById(id);
            if(resp.success === true){
                setLoading(false);
                setCentresData(resp?.collections);
            }else{
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const fetchCentreUsers = async (centreId) => {
        try {
            const resp = await getCentreUsers(centreId);
            if (resp?.success === true) {
                setStaffUsers(resp?.collections || []);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error');
        }
    };

    const fetchCentreRoles = async () => {
        try {
            const resp = await getRolesByGroupName({ roleGroup: 'FC-A' });
            if (resp?.success === true) {
                setCentreRoles(resp.collections.filter((r: any) => r.id !== 5));
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load roles');
        }
    };

    // Unassigning is role-specific: a user can hold more than one centre role
    // here (e.g. Centre Manager and Mentor), so removing them must drop only
    // the role that was clicked, not every centre-role grant they hold.
    const handleRemoveUser = (user: any, role: any) => {
        confirmAlert({
            customUI: ({ onClose }) => (
                <div className="card p-4" style={{ minWidth: 320 }}>
                    <h5>Remove Role</h5>
                    <p>Remove <strong>{role.name}</strong> role from <strong>{user.first_name} {user.last_name}</strong> at this centre?</p>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-danger btn-sm mr-2" onClick={async () => {
                            onClose();
                            try {
                                const resp = await unassignUserFromCentre({ user_id: user.id, centre_id: id, role_id: role.id });
                                if (resp?.success === true) {
                                    toast.success(`${role.name} role removed from user`);
                                    fetchCentreUsers(id);
                                } else {
                                    toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to remove role');
                                }
                            } catch (error: any) {
                                toast.error(error?.message || 'Internal Server Error');
                            }
                        }}>Remove</button>
                        <button className="btn btn-default btn-sm" onClick={onClose}>Cancel</button>
                    </div>
                </div>
            )
        });
    };

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-primary">
                            <div className="card-header">
                                <h3 className="card-title">Manage Centre Staff/Users</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Franchise</label>
                                            <dd>{centresData.franchise_name || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Postcode</label>
                                            <dd>{centresData.postcode_name || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Centre Name</label>
                                            <dd>{centresData.name || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Area Code</label>
                                            <dd>{centresData.area_code || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Email</label>
                                            <dd>{centresData.email || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Phone Number</label>
                                            <dd>{centresData.phone_number || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Address</label>
                                            <dd>{centresData.address || '---'}</dd>
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <div className="form-group">
                                            <label>Status</label>
                                            <dd>
                                                {centresData.status === 'active'
                                                    ? <span className="badge bg-success">Active</span>
                                                    : <span className="capitalize badge bg-danger">{centresData.status}</span>
                                                }
                                            </dd>
                                        </div>
                                    </div>
                                </div>

                                <hr />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0"><strong>Centre Staff/Users</strong></h6>
                                    {!isArchived && (
                                        <div>
                                            <button className="btn btn-sm btn-default mr-2" onClick={() => { setEditUser(null); setShowAddUser(true); }}>
                                                + Add New User
                                            </button>
                                            <button className="btn btn-sm btn-primary" onClick={() => setShowAssignUser(true)}>
                                                Assign Existing User
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <table className="table table-bordered table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role(s) Attached to Centre</th>
                                            <th>Status</th>
                                            {!isArchived && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {staffUsers.length > 0 ? staffUsers.map((user: any) => (
                                            <tr key={user.id}>
                                                <td>{user.first_name} {user.last_name}</td>
                                                <td>{user.email}</td>
                                                <td>
                                                    {user.roles?.filter((r: any) => centreRoles.some((cr: any) => cr.slug === r.slug)).map((r: any) => (
                                                        <span key={r.id} className="badge badge-light border mr-1 d-inline-flex align-items-center">
                                                            {r.name}
                                                            {!isArchived && (
                                                                <a
                                                                    href='#'
                                                                    className="ml-1 text-danger"
                                                                    title={`Remove ${r.name} role from this centre`}
                                                                    onClick={(e) => { e.preventDefault(); handleRemoveUser(user, r); }}
                                                                >
                                                                    &times;
                                                                </a>
                                                            )}
                                                        </span>
                                                    ))}
                                                    {!user.roles?.some((r: any) => centreRoles.some((cr: any) => cr.slug === r.slug)) && '---'}
                                                </td>
                                                <td>
                                                    {user.status === 'active'
                                                        ? <span className="badge badge-success">Active</span>
                                                        : <span className="badge badge-secondary" style={{ textTransform: 'capitalize' }}>{user.status}</span>
                                                    }
                                                </td>
                                                {!isArchived && (
                                                    <td>
                                                        <button className="btn btn-primary btn-xs mr-1" title="Edit User" onClick={() => { setEditUser(user); setShowAddUser(true); }}>
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={isArchived ? 4 : 5} className="text-center text-muted">No staff users assigned</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer">
                                <Link className="btn btn-default" to='/centers'>Close</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <AddUpdateCentreUserModal
            show={showAddUser}
            handleClose={() => { setShowAddUser(false); setEditUser(null); }}
            franchiseId={centresData?.franchise_id}
            centreRoles={centreRoles}
            editUser={editUser}
            onSuccess={() => fetchCentreUsers(id)}
        />

        <AssignExistingUserModal
            show={showAssignUser}
            handleClose={() => setShowAssignUser(false)}
            franchiseId={centresData?.franchise_id}
            centreId={id}
            centreRoles={centreRoles}
            existingStaff={staffUsers}
            onSuccess={() => fetchCentreUsers(id)}
        />
    </div>
  )
}

export default ViewCentre;
